/**
 * Pure helpers shared by the People screens. Dashboard/tone/colour decisions
 * live here so the payroll page and the desk can never disagree about what a
 * status means.
 */
import type {
  AttendanceStatus,
  LeaveRequestStatus,
  PayrollRunStatus,
  ShiftStatus,
} from "../api/types";
import type { Badge } from "../components/ui";

type Tone = NonNullable<Parameters<typeof Badge>[0]["tone"]>;

export const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  present: "Present",
  half_day: "Half day",
  absent: "Absent",
  on_leave: "On leave",
  holiday: "Holiday",
  week_off: "Week off",
};

export const ATTENDANCE_TONE: Record<AttendanceStatus, Tone> = {
  present: "emerald",
  half_day: "amber",
  absent: "rose",
  on_leave: "sky",
  holiday: "violet",
  week_off: "slate",
};

export const LEAVE_LABEL: Record<LeaveRequestStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export const LEAVE_TONE: Record<LeaveRequestStatus, Tone> = {
  pending: "amber",
  approved: "emerald",
  rejected: "rose",
  cancelled: "slate",
};

export const PAYROLL_LABEL: Record<PayrollRunStatus, string> = {
  draft: "Draft",
  computed: "Computed",
  approved: "Approved",
  paid: "Paid",
  locked: "Locked",
  cancelled: "Cancelled",
};

export const PAYROLL_TONE: Record<PayrollRunStatus, Tone> = {
  draft: "slate",
  computed: "sky",
  approved: "amber",
  paid: "emerald",
  locked: "violet",
  cancelled: "rose",
};

export const SHIFT_LABEL: Record<ShiftStatus, string> = {
  draft: "Draft",
  published: "Published",
  cancelled: "Cancelled",
};

export const SHIFT_TONE: Record<ShiftStatus, Tone> = {
  draft: "slate",
  published: "emerald",
  cancelled: "rose",
};

/** 495 minutes -> "8h 15m"; an open row's null total reads as "—".
 *
 * Accepts a string too: the API serialises a decimal duration as a string, and
 * coercing here keeps every caller from repeating `Number(...)`.
 */
export function formatMinutes(total: number | string | null | undefined): string {
  if (total == null || total === "") return "—";
  const value = typeof total === "string" ? Number(total) : total;
  if (Number.isNaN(value)) return "—";
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Who may act, not what they can see. "manager and above" is the People
 * pillar's write line: assigning work, deciding leave, and touching pay are
 * manager territory per the viewset docstrings.
 */
export function isManagerOrAbove(role: string | undefined): boolean {
  return role === "owner" || role === "admin" || role === "manager";
}

export function isAccountAdmin(role: string | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * The reporting-manager state for an employee record.
 *
 * `owner` is the root of the hierarchy and must never be told to set a manager;
 * `missing` is an ordinary employee who genuinely needs one for leave routing.
 */
export function reportingManagerState(record: {
  reporting_to?: string | null;
  is_account_owner?: boolean;
}): "assigned" | "owner" | "missing" {
  if (record.reporting_to) return "assigned";
  return record.is_account_owner ? "owner" : "missing";
}